import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '@/lib/api'
import { formatPercent, formatDate } from '@/lib/utils'
import { LoadingSpinner } from '@/components/ui'
import { Check, AlertCircle } from 'lucide-react'

const PERFORMANCE_TYPES = [
  { id: 1, label: 'LIVE' },
  { id: 2, label: 'BACKTEST' },
  { id: 3, label: 'ESTIMATED' },
]

export default function InsertReturns() {
  const queryClient = useQueryClient()
  const [selectedFundId, setSelectedFundId] = useState<number | null>(null)
  const [returnDate, setReturnDate] = useState('')
  const [returnValue, setReturnValue] = useState('')
  const [performanceTypeId, setPerformanceTypeId] = useState(1)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  // Load funds for dropdown
  const { data: funds, isLoading: fundsLoading } = useQuery({
    queryKey: ['data-funds'],
    queryFn: () => api.get('/data/funds').then((res) => res.data),
  })

  // Load recent returns for selected fund
  const { data: recentReturns, isLoading: returnsLoading } = useQuery({
    queryKey: ['recent-returns', selectedFundId],
    queryFn: () => api.get(`/data/returns/${selectedFundId}/recent`).then((res) => res.data),
    enabled: !!selectedFundId,
  })

  // Insert mutation
  const insertMutation = useMutation({
    mutationFn: (payload: { fund_id: number; return_date: string; return_value: number; performance_type_id: number }) =>
      api.post('/data/returns', payload),
    onSuccess: () => {
      setMessage({ type: 'success', text: 'Return inserted successfully' })
      setReturnValue('')
      setReturnDate('')
      queryClient.invalidateQueries({ queryKey: ['recent-returns', selectedFundId] })
      setTimeout(() => setMessage(null), 3000)
    },
    onError: (err: any) => {
      const detail = err.response?.data?.detail || 'Failed to insert return'
      setMessage({ type: 'error', text: detail })
      setTimeout(() => setMessage(null), 5000)
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedFundId || !returnDate || !returnValue) return

    const numValue = parseFloat(returnValue) / 100 // Convert percentage to decimal
    if (isNaN(numValue) || numValue < -1 || numValue > 1) {
      setMessage({ type: 'error', text: 'Return must be between -100% and +100%' })
      return
    }

    insertMutation.mutate({
      fund_id: selectedFundId,
      return_date: returnDate,
      return_value: numValue,
      performance_type_id: performanceTypeId,
    })
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Insert Returns</h1>
        <p className="text-muted-foreground">Enter fund returns data</p>
      </div>

      {/* Message banner */}
      {message && (
        <div
          className={`flex items-center gap-2 p-3 rounded-lg text-sm ${
            message.type === 'success'
              ? 'bg-green-50 text-green-700 border border-green-200'
              : 'bg-red-50 text-red-700 border border-red-200'
          }`}
        >
          {message.type === 'success' ? <Check className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
          {message.text}
        </div>
      )}

      {/* Insert form */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h2 className="text-lg font-semibold mb-4">New Return Entry</h2>
        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
          {/* Fund selector */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-muted-foreground mb-1">Fund</label>
            {fundsLoading ? (
              <div className="h-10 flex items-center"><span className="spinner" /></div>
            ) : (
              <select
                className="w-full h-10 px-3 border border-input rounded-md bg-background text-sm"
                value={selectedFundId || ''}
                onChange={(e) => setSelectedFundId(e.target.value ? parseInt(e.target.value) : null)}
              >
                <option value="">Select a fund...</option>
                {funds?.map((f: any) => (
                  <option key={f.fund_id} value={f.fund_id}>{f.name}</option>
                ))}
              </select>
            )}
          </div>

          {/* Date */}
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-1">Date</label>
            <input
              type="date"
              className="w-full h-10 px-3 border border-input rounded-md bg-background text-sm"
              value={returnDate}
              onChange={(e) => setReturnDate(e.target.value)}
            />
          </div>

          {/* Return value */}
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-1">Return (%)</label>
            <input
              type="number"
              step="0.01"
              min="-100"
              max="100"
              className="w-full h-10 px-3 border border-input rounded-md bg-background text-sm"
              placeholder="e.g. 2.5"
              value={returnValue}
              onChange={(e) => setReturnValue(e.target.value)}
            />
          </div>

          {/* Type + Submit */}
          <div className="flex gap-2">
            <select
              className="h-10 px-2 border border-input rounded-md bg-background text-sm"
              value={performanceTypeId}
              onChange={(e) => setPerformanceTypeId(parseInt(e.target.value))}
            >
              {PERFORMANCE_TYPES.map((t) => (
                <option key={t.id} value={t.id}>{t.label}</option>
              ))}
            </select>
            <button
              type="submit"
              disabled={!selectedFundId || !returnDate || !returnValue || insertMutation.isPending}
              className="h-10 px-4 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {insertMutation.isPending ? 'Saving...' : 'Insert'}
            </button>
          </div>
        </form>
      </div>

      {/* Recent returns table */}
      {selectedFundId && (
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="p-4 border-b border-border">
            <h2 className="text-lg font-semibold">
              Recent Returns
              {funds && (
                <span className="text-sm font-normal text-muted-foreground ml-2">
                  — {funds.find((f: any) => f.fund_id === selectedFundId)?.name}
                </span>
              )}
            </h2>
          </div>
          {returnsLoading ? (
            <LoadingSpinner />
          ) : recentReturns?.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">No returns found for this fund</div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th className="text-right">Return</th>
                  <th>Type</th>
                </tr>
              </thead>
              <tbody>
                {recentReturns?.map((r: any, i: number) => (
                  <tr key={i}>
                    <td>{formatDate(r.return_date)}</td>
                    <td className={`text-right font-mono ${r.return_value >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatPercent(r.return_value)}
                    </td>
                    <td className="text-muted-foreground">
                      {PERFORMANCE_TYPES.find((t) => t.id === r.performance_type_id)?.label || r.performance_type_id}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  )
}
